<template>
  <vl-grid v-if="data.defaultTests && data.defaultTests.length > 0" v-vl-spacer:bottom.medium>
    <vl-column width="12" width-xs="12">
      <vl-title tag-name="h2">{{ $t('deliveryprocessdefinition.geckoSync.defaultTests') }}</vl-title>
    </vl-column>
    <vl-column width="12" width-xs="12">
      <vl-grid>
        <vl-column v-vl-spacer:bottom.medium width="12">
          <div>
            <vl-annotation>
              {{ $t('deliveryprocessdefinition.geckoSync.defaultTestsDescription') }}
            </vl-annotation>
          </div>
          <div>
            <vl-annotation>
              {{ $t('deliveryprocessdefinition.geckoSync.defaultTestsDisableDescription') }}
            </vl-annotation>
          </div>
        </vl-column>
        <vl-column width="12">
          <vl-accordion-list>
            <vl-accordion-list-item v-for="(testControlType, i) in testControlTypes" :id="i" :key="i">
              <vl-accordion :id="i.toString()" v-vl-spacer:bottom.medium :open="false">
                <template v-slot:title>
                  <span>{{ getControlTypeLabel(testControlType) }}</span>
                  &nbsp;<span
                    v-if="disabledControlTypesForDefaultTests.includes(testControlType)"
                    :title="t('deliveryprocessdefinition.geckoSync.defaultTestsHaveBeenDisabled')"
                    class="vl-icon vl-vi vl-vi-alert-circle"
                  >
                  </span>
                </template>
                <template v-slot:subtitle>
                  <vl-annotation>
                    {{
                      $t('deliveryprocessdefinition.geckoSync.' + getControlTypeName(testControlType) + 'Description')
                    }}
                  </vl-annotation>
                </template>
                <vl-grid>
                  <vl-column width="12">
                    <GeckoTestDatatable
                      :items="getDefaultTestsByControlType(testControlType)"
                      :isReadOnly="isReadOnly"
                      :deliveryQualityCheckTypeId="0"
                      :onCheckboxChange="onCheckboxChanged"
                    ></GeckoTestDatatable>
                  </vl-column>
                </vl-grid>
              </vl-accordion>
            </vl-accordion-list-item>
          </vl-accordion-list>
        </vl-column>
      </vl-grid>
    </vl-column>
  </vl-grid>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, reactive, watch } from 'vue';
import { GeckoTestDto } from '@/api/backoffice/clients';
import GeckoTestDatatable from '../../subcomponents/gecko-test-datatable/GeckoTestDatatable.vue';
import { DeliveryQualityCheckData } from '../../subcomponents/gecko-test-datatable/GeckoTestActiveToggle.vue';

const { t } = useI18n();

const props = defineProps<{
  defaultTests: GeckoTestDto[];
  isReadOnly: boolean;
  onCheckboxChange?: (evt: DeliveryQualityCheckData) => Promise<boolean>;
}>();

const data = reactive({
  defaultTests: [] as GeckoTestDto[]
});

watch(
  () => props.defaultTests,
  () => {
    data.defaultTests = props.defaultTests?.map((test) => ({ ...test })) || [];
  },
  { immediate: true }
);

const testControlTypes = computed(() => {
  return data.defaultTests.reduce((controlTypes, test) => {
    if (!controlTypes.includes(test.controlType)) {
      controlTypes.push(test.controlType);
    }
    return controlTypes;
  }, [] as number[]);
});

const getDefaultTestsByControlType = (controlType: number): GeckoTestDto[] => {
  return data.defaultTests.filter((test) => test.controlType === controlType);
};

const getControlTypeName = (controlType: number): string => {
  switch (controlType) {
    case 1:
      return 'initialChecks';
    case 2:
      return 'criticalChecks';
    case 3:
      return 'automaticChecks';
    case 4:
      return 'semiAutomaticChecks';
    default:
      return 'noChecks';
  }
};

const getControlTypeLabel = (controlType: number): string => {
  switch (controlType) {
    case 0:
      return 'Geen controle';
    case 1:
      return 'Initiële controle';
    case 2:
      return 'Kritieke controle';
    case 3:
      return 'Automatische controle';
    case 4:
      return 'Semi-automatische controle';
    default:
      return 'Onbekend';
  }
};

const disabledControlTypesForDefaultTests = computed(() => {
  const codes = data.defaultTests.filter((x) => x.test.availability == 'disabled').map((dqc) => dqc.test.code);
  const controlTypes = data.defaultTests
    .filter((test) => codes.includes(test.test.code))
    .map((test) => test.controlType);
  return Array.from(new Set(controlTypes));
});

const onCheckboxChanged = async (deliveryQualityCheckData: DeliveryQualityCheckData): Promise<boolean> => {
  const succeeded = await props.onCheckboxChange?.(deliveryQualityCheckData);

  if (succeeded) {
    updateDefaultTestAvailability(deliveryQualityCheckData.test.code, deliveryQualityCheckData.isEnabled);
  }

  return succeeded;
};

const updateDefaultTestAvailability = (testCode: string, isEnabled: boolean) => {
  data.defaultTests.forEach((test) => {
    if (test.test.code === testCode) {
      test.test.availability = isEnabled ? 'enabled' : 'disabled';
    }
  });
};
</script>

<style lang="scss" scoped></style>
